import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    Box,
    Flex,
    Text,
    Input,
    Button,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    FormControl,
    FormLabel,
    useToast
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Textarea} from '@chakra-ui/react'
import {useCurrentCustomer} from '../../hooks/use-current-customer'
import {useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import {WISHLIST_NAME_EXIST, WISHLIST_CREATED, WISHLIST_ERROR} from '../../constants'

const CreateNewListModal = ({isOpen, onClose, wishLists}) => {
    const [listName, setListName] = useState('')
    const [description, setDescription] = useState('')
    const [charCount, setCharCount] = useState(300)

    const toast = useToast()
    const intl = useIntl()
    const {data: customer} = useCurrentCustomer()
    const {customerId} = customer || {}

    const createCustomerProductList = useShopperCustomersMutation('createCustomerProductList')

    const handleDescriptionChange = (e) => {
        const value = e.target.value
        setDescription(value)
        setCharCount(300 - value.length)
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        const wishListName = listName?.trim()

        if (!wishListName || !customerId) return

        const isDuplicateWishlist = wishLists?.some(
            (list) => list?.name?.toLowerCase() === wishListName.toLowerCase()
        )

        if (isDuplicateWishlist) {
            toast({
                title: intl.formatMessage(WISHLIST_NAME_EXIST),
                description: intl.formatMessage(
                    {
                        id: 'wishlist.toast_duplicate_description',
                        defaultMessage:
                            'A list named "{name}" already exists. Please choose a different name.'
                    },
                    {name: wishListName}
                ),
                status: 'error',
                duration: 5000,
                isClosable: true
            })

            return
        }

        try {
            await createCustomerProductList.mutateAsync({
                parameters: {customerId},
                body: {
                    type: 'wish_list',
                    name: wishListName,
                    description: description.trim()
                }
            })

            toast({
                title: intl.formatMessage(WISHLIST_CREATED),
                description: intl.formatMessage(
                    {
                        id: 'wishlist.toast_success_description',
                        defaultMessage: 'Your list "{name}" was created successfully.'
                    },
                    {name: wishListName}
                ),
                status: 'success',
                duration: 4000,
                isClosable: false
            })

            setListName('')
            setDescription('')
            setCharCount(300)
            onClose()
        } catch (error) {
            toast({
                title: intl.formatMessage(WISHLIST_ERROR),
                description:
                    error?.message ||
                    intl.formatMessage({
                        id: 'wishlist.toast_error_fallback',
                        defaultMessage: 'Something went wrong.'
                    }),
                status: 'error',
                duration: 5000,
                isClosable: true
            })
        }
    }

    useEffect(() => {
        if (!isOpen) {
            setListName('')
            setDescription('')
            setCharCount(300)
        }
    }, [isOpen])

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent maxW="md" borderRadius="md">
                <ModalHeader>
                    <FormattedMessage defaultMessage="Create New List" id="wishlist.modal_title" />
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <form onSubmit={handleSubmit}>
                        <Box mb={4}>
                            <FormControl isRequired>
                                <FormLabel>
                                    <FormattedMessage
                                        defaultMessage="Name your new list"
                                        id="wishlist.label_new_list"
                                    />
                                </FormLabel>
                                <Input
                                    id="new-wishlist-name"
                                    value={listName}
                                    onChange={(e) => setListName(e.target.value)}
                                    maxLength={20}
                                    pattern="^[^\s]+( [^\s]+)*$"
                                    required
                                />
                            </FormControl>
                        </Box>

                        <Box mb={4}>
                            <FormControl>
                                <Flex justify="space-between">
                                    <FormLabel htmlFor="new-wishlist-description">
                                        <FormattedMessage
                                            defaultMessage="Notes for list"
                                            id="wishlist.label_note_list"
                                        />
                                    </FormLabel>
                                    <Text
                                        id="char-count-helper"
                                        fontSize="sm"
                                        color="gray.500"
                                        aria-live="polite"
                                    >
                                        {charCount} characters left
                                    </Text>
                                </Flex>
                                <Textarea
                                    id="new-wishlist-description"
                                    value={description}
                                    onChange={handleDescriptionChange}
                                    maxLength={300}
                                    placeholder="Add a note or description for your list"
                                    rows={5}
                                    aria-describedby="char-count-helper"
                                />
                            </FormControl>
                        </Box>

                        <Flex justify="flex-end" gap={3} mt={6}>
                            <Button variant="outline" onClick={onClose}>
                                <FormattedMessage
                                    defaultMessage="Cancel"
                                    id="wishlist.button_cancel"
                                />
                            </Button>
                            <Button
                                colorScheme="orange"
                                type="submit"
                                isLoading={createCustomerProductList.isPending}
                            >
                                <FormattedMessage
                                    defaultMessage="Create List"
                                    id="wishlist.button_create_list"
                                />
                            </Button>
                        </Flex>
                    </form>
                </ModalBody>
            </ModalContent>
        </Modal>
    )
}

CreateNewListModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    wishLists: PropTypes.array
}

export default CreateNewListModal
